import React, { useState, useEffect } from "react";
import * as _ from "lodash";
import HurdleView from "underwriter_dashboard/shared/HurdleView";
import { EditingState } from "web_modules/shared/BaseTable";
import {
  TaskShape,
  TaskStage,
  TaskOwner,
  TASK_STATUS_OPTIONS,
  TaskConfigParamOptions,
  TaskStatus,
} from "@eave/shared/types/api";
import { UserRole } from "@eave/shared/types/application";
import { LoadingState } from "underwriter_dashboard/shared/types";
import {
  Button,
  ContentSwitcher,
  InlineNotification,
  Loading,
  Modal,
  RadioButtonGroup,
  RadioButton,
  Switch,
} from "carbon-components-react";
import {
  getTasksForApplication,
  updateTaskForApplication,
  createTask,
  getOperators,
  getTaskOptions,
  batchUpdateTasksForApplication,
} from "underwriter_dashboard/shared/Tasks/APIHelpers";
import { HurdleProps } from "underwriter_dashboard/HurdleEditor";
import getHandlers from "web_modules/shared/FormHelpers/eventHandlers";
import Validations from "web_modules/shared/FormHelpers/validation";

import TasksForRole from "./TasksForRole";
import TaskForm from "./TaskForm";

export interface TaskFormShape extends TaskShape {
  default_index_order: number; // current index_order differs from default_index_order as defined by `newTaskDefaultIndexOrder`
  selectedRawRowsData: any[];

  include_in_queues?: boolean;
}

interface TasksProps extends HurdleProps {}

const Tasks: React.FC<TasksProps> = (props: TasksProps) => {
  const { app, hurdle, onHide } = props;
  const { id } = app;

  const [tasksList, setTasks] = useState([]);

  const initialTaskFormState = {
    due_date_params: {},
  };
  const [taskFormState, updateTaskFormState] = useState({} as TaskFormShape);
  const [invalidFields, updateInvalidFields] = useState([]);

  // UI-related states
  const [tasksModalIsOpen, updateTaskModalIsOpen] = useState(false);
  const [disableModalSubmit, setDisableModalSubmit] = useState(false);
  const [batchEditModalIsOpen, updateBatchEditModalIsOpen] = useState(false);
  const [editingMode, updateEditingMode] = useState(EditingState.INACTIVE);
  const [loadingState, updateLoadingState] = useState(LoadingState.INACTIVE);

  const handlers = getHandlers(
    taskFormState,
    updateTaskFormState,
    invalidFields,
    updateInvalidFields,
  );

  const taskStatusRadioGroup = () => (
    <RadioButtonGroup
      valueSelected={taskFormState && taskFormState.status}
      labelPosition="right"
      legend="Task status *"
      name="status"
      id="task-status"
      invalid={invalidFields.includes("status")}
      onChange={status => handlers.handleRadioChange(status, "status", [Validations.IsRequired])}
      orientation="horizontal"
    >
      {TASK_STATUS_OPTIONS.map(status => (
        <RadioButton
          id={`radio-${status}`}
          key={`radio-${status}`}
          labelText={status}
          value={status}
        />
      ))}
    </RadioButtonGroup>
  );

  // // Error message for the inline notification
  const [errorMessage, updateErrorMessage] = useState("");

  const [operators, setOperators] = React.useState(null);

  const filterPreferenceKey = "TRUSSelectedTaskRole";
  const [selectedTaskRole, updateSelectedQueueRole] = useState<UserRole>(
    (localStorage.getItem(filterPreferenceKey) as UserRole) || UserRole.LoanManager,
  );

  // Since each stage gets its own BaseTable, let's extract shared props here to DRY it up
  const sharedBaseTableProps = {
    style: {
      marginBottom: "1rem",
    },
    openModal: () => updateTaskModalIsOpen(true),
    openBatchEditModal: () => updateBatchEditModalIsOpen(true),

    updateEditingMode: m => updateEditingMode(m),

    disableBatchDelete: true,
    disableOverflowDelete: true,
  };

  const sharedProps = {
    operators,
    setTasks,
    updateLoadingState,
    updateErrorMessage,
    updateTaskFormState,
    updateTaskModalIsOpen,
  };

  const changeSelectedQueueRole = selectedOption => {
    updateSelectedQueueRole(selectedOption.name);
    localStorage.setItem(filterPreferenceKey, selectedOption.name);
  };

  const selectedTaskRoleOptions = [
    {
      text: "Underwriters",
      name: UserRole.Underwriter,
    },
    {
      text: "Loan Managers",
      name: UserRole.LoanManager,
    },
  ];

  const [taskOptions, setTaskOptions] = useState<TaskConfigParamOptions>(null);

  // Load initial applications
  useEffect(() => {
    updateLoadingState(LoadingState.ACTIVE);

    const gtfaPromise = getTasksForApplication(id).then((response: any) => {
      setTasks(response.tasks);
    });

    const goPromise = getOperators().then((ops: any) => {
      const opsMap = _.chain(ops).keyBy("id").value();
      setOperators(opsMap);
    });

    const getTaskOptionsPromise = getTaskOptions().then((r: TaskConfigParamOptions) => {
      setTaskOptions(r);
    });

    const promises = [gtfaPromise, goPromise, getTaskOptionsPromise];
    Promise.all(promises).then(() => {
      updateLoadingState(LoadingState.FINISHED);
    });
  }, [id]);

  const resetFormState = () => {
    updateTaskFormState(initialTaskFormState as TaskFormShape);
    updateLoadingState(LoadingState.FINISHED);
    updateEditingMode(EditingState.INACTIVE);
    updateBatchEditModalIsOpen(false);
    updateTaskModalIsOpen(false);
  };

  const dueDateParamInputsAreValid = (): Boolean => {
    // Permit null values (user may not want to edit due_date_params for a new task)
    if (!taskFormState.due_date_params) {
      return true;
    }

    return (
      Validations.IsNumber(taskFormState.due_date_params.days) &&
      Validations.IsValid(taskFormState.due_date_params.type_of_day) &&
      Validations.IsValid(taskFormState.due_date_params.before_or_after) &&
      Validations.IsValid(taskFormState.due_date_params.reference_date)
    );
  };

  return (
    <HurdleView app={app} hurdle={hurdle} onHide={onHide}>
      {tasksList && (
        <>
          <Loading active={loadingState === LoadingState.ACTIVE} />
          <Modal
            open={batchEditModalIsOpen}
            modalHeading="Update Status"
            primaryButtonText="Save"
            primaryButtonDisabled={
              !(
                taskFormState &&
                taskFormState.selectedRawRowsData &&
                taskFormState.selectedRawRowsData.length > 0 &&
                taskFormState.status !== undefined
              )
            }
            secondaryButtonText="Cancel"
            onBlur={() => {
              updateTaskFormState(null);
              updateBatchEditModalIsOpen(false);
            }}
            onRequestSubmit={() => {
              if (invalidFields.length > 0) {
                updateErrorMessage("There are invalid fields, please check again");
                return;
              }

              updateLoadingState(LoadingState.ACTIVE);

              batchUpdateTasksForApplication(
                app.id,
                taskFormState.selectedRawRowsData,
                taskFormState.status as TaskStatus,
              )
                .then(() => {
                  getTasksForApplication(app.id).then((response: any) => {
                    setTasks(response.tasks);
                    resetFormState();
                  });
                })
                .catch(err => {
                  updateLoadingState(LoadingState.FINISHED);
                  updateErrorMessage(err);
                });
            }}
            onRequestClose={() => {
              updateTaskFormState(null);
              updateBatchEditModalIsOpen(false);
            }}
          >
            {taskFormState &&
              taskFormState.selectedRawRowsData &&
              taskFormState.selectedRawRowsData.length > 0 && (
                <>
                  <Loading active={loadingState === LoadingState.ACTIVE} />
                  {taskStatusRadioGroup()}
                  <div style={{ height: "10px" }} />
                </>
              )}
          </Modal>

          <Modal
            hasForm
            open={tasksModalIsOpen}
            modalHeading={editingMode === EditingState.CREATE ? "Add Task" : "Update Task"}
            primaryButtonText={editingMode === EditingState.CREATE ? "Save" : "Update"}
            secondaryButtonText="Cancel"
            selectorPrimaryFocus="#task-status"
            primaryButtonDisabled={
              disableModalSubmit ||
              !(
                taskFormState !== null &&
                Validations.IsValid(taskFormState.status) &&
                Validations.IsValid(taskFormState.name) &&
                Validations.IsValid(taskFormState.stage) &&
                Validations.IsValid(taskFormState.category) &&
                Validations.IsValid(taskFormState.owner) &&
                dueDateParamInputsAreValid()
              )
            }
            onRequestSubmit={() => {
              if (invalidFields.length > 0) {
                updateErrorMessage("There are invalid fields, please check again");
                return;
              }

              if (editingMode === EditingState.CREATE) {
                updateLoadingState(LoadingState.ACTIVE);
                createTask(app.id, taskFormState)
                  .then(() => {
                    getTasksForApplication(app.id).then((response: any) => {
                      setTasks(response.tasks);
                      resetFormState();
                    });
                  })
                  .catch(err => {
                    updateLoadingState(LoadingState.FINISHED);
                    updateErrorMessage(err);
                  });
              }
              if (editingMode === EditingState.UPDATE) {
                updateLoadingState(LoadingState.ACTIVE);
                updateTaskForApplication(app.id, taskFormState.id, taskFormState)
                  .then(() => {
                    getTasksForApplication(app.id).then((response: any) => {
                      setTasks(response.tasks);
                      resetFormState();
                    });
                  })
                  .catch(err => {
                    updateLoadingState(LoadingState.FINISHED);
                    updateErrorMessage(err);
                  });
              }
            }}
            onRequestClose={() => {
              updateInvalidFields([]);
              updateTaskFormState(null);
              updateTaskModalIsOpen(false);
            }}
            onBlur={() => {
              updateTaskFormState(null);
              updateTaskModalIsOpen(false);
            }}
          >
            {errorMessage.length > 0 && (
              <InlineNotification
                kind="error"
                notificationType="inline"
                role="alert"
                statusIconDescription="Error"
                subtitle={errorMessage}
                title="Error"
                onCloseButtonClick={() => {
                  updateErrorMessage("");
                }}
              />
            )}

            <p>Required fields are marked with an asterisk *</p>

            <TaskForm
              taskOptions={taskOptions}
              taskFormState={taskFormState}
              updateTaskFormState={updateTaskFormState}
              invalidFields={invalidFields}
              updateInvalidFields={updateInvalidFields}
              editingMode={editingMode}
              tasksList={tasksList}
              handlers={handlers}
              setDisableModalSubmit={setDisableModalSubmit}
            />
          </Modal>

          <div style={{ position: "relative" }}>
            <ContentSwitcher
              style={{
                position: "absolute",
                top: "-48px",
                left: "180px",
                width: "400px",
                zIndex: 12,
              }}
              onChange={action => {
                changeSelectedQueueRole(action);
              }}
              selectedIndex={selectedTaskRoleOptions
                .map(item => item.name)
                .indexOf(selectedTaskRole)}
            >
              {selectedTaskRoleOptions.map(item => (
                <Switch key={item.name} name={item.name} text={item.text} />
              ))}
            </ContentSwitcher>
          </div>
          {selectedTaskRole === UserRole.Underwriter && (
            <TasksForRole
              app={app}
              allTasks={tasksList}
              taskOwner={TaskOwner.Underwriter}
              operators={operators}
              setTasks={setTasks}
              stagesForRole={[TaskStage.PreApplication, TaskStage.RESPA]}
              updateErrorMessage={updateErrorMessage}
              tableProps={sharedBaseTableProps}
              {...sharedProps}
            />
          )}
          {selectedTaskRole === UserRole.LoanManager && (
            <TasksForRole
              app={app}
              allTasks={tasksList}
              taskOwner={TaskOwner.LMLender}
              operators={operators}
              setTasks={setTasks}
              stagesForRole={[
                TaskStage.PreProcessing,
                TaskStage.Processing,
                TaskStage.Closing,
                TaskStage.PostClosing,
              ]}
              updateErrorMessage={updateErrorMessage}
              tableProps={sharedBaseTableProps}
              {...sharedProps}
            />
          )}
        </>
      )}
      <br />
      <footer>
        <Button onClick={onHide}>
          <i className="fa fa-thumbs-up" /> OK
        </Button>
      </footer>
    </HurdleView>
  );
};

export default Tasks;
